<template>
    <div>
        <!-- Photo Dialog -->
        <ItemPhotoDialog v-model="showPhotoDialog"
                         :photos="selectedPhotos" />

        <!-- The table (paging, sort, search, reloads, both layouts) is DuckDbDataTable's; the issue
             row, the mobile card and the report button are this grid's slots -->
        <DuckDbDataTable :title="t('issuesGrid')"
                         :headers="headers"
                         :fetch-page="fetchPage"
                         :filter-context="filterContext"
                         :data-loaded="dataLoaded"
                         :reload-key="reloadKey"
                         :default-sort="defaultSort"
                         :items-per-page-options="pageSizes"
                         item-value="id">
            <!-- Toolbar Actions -->
            <template #toolbar-actions>

                <v-btn v-if="generateReport"
                       icon
                       variant="plain"
                       @click="showSummaryReportDialog = true">
                    <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>

            <!-- Desktop Row -->
            <template #desktop-row="{ item }">
                <tr style="height: 100px!important;">
                    <td class="client-id-cell">
                        <div class="d-flex align-center">
                            <v-chip label variant="text">
                                <a @click.prevent="openItemUrl(item)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.clientId }}
                                </a>
                            </v-chip>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex justify-center">
                            <div v-if="item.photo || item.responsePhoto" class="photo-stack" @click="openPhotoDialog(item)">
                                <div class="photo-card photo-card-back">
                                    <v-img :src="item.photo"
                                           width="60"
                                           height="60"
                                           :aspect-ratio="4/3"
                                           contain
                                           class="rounded cursor-pointer" />
                                </div>
                                <div v-if="item.responsePhoto" class="photo-card photo-card-front">
                                    <v-img :src="item.responsePhoto"
                                           width="60"
                                           height="60"
                                           :aspect-ratio="4/3"
                                           contain
                                           class="rounded cursor-pointer" />
                                </div>
                            </div>
                            <span v-else class="text-caption text-disabled">{{ t('noPhoto') }}</span>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start gap-1" style="width: 280px;">
                            <div class="text-subtitle-2">
                                {{ item.issueCategory }}
                            </div>
                            <div class="">
                                {{ item.issueType }}
                            </div>
                            <div class="">
                                {{ item.issueItem }}
                            </div>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start" style="width: 250px;">
                            <span>{{ item.issueDescription?.replace(/[\r\n]+/g, '; ') }}</span>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start gap-1" style="width: 150px; gap: 8px;">
                            <span class="text-start ga-1">{{ item.responsibleParty }}</span>
                            <v-chip variant="tonal"
                                    size="small"
                                    class="text-start"
                                    label>
                                {{ item.priority }}
                            </v-chip>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start ga-1">
                            <template v-if="item.dueBy">
                                <v-chip :color="getDaysLeft(item) < 0 ? 'error' : 'default'"
                                        :variant="getDaysLeft(item) < 0 ? 'tonal' : 'flat'"
                                        label
                                        class="text-start">
                                    {{ formatDate(item.dueBy) }}
                                </v-chip>
                                <span class="text-caption">
                                    {{ getDaysLeftText(item) }}
                                </span>
                            </template>
                            <span v-else class="text-disabled">{{ t('notSet') }}</span>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start" style="width: 180px; gap: 4px;">
                            <div>
                                <v-icon icon="mdi-map" size="small" class="mr-1"></v-icon>
                                <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}`)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.drawingTitle }}
                                </a>
                            </div>
                            <div>
                                <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                                <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.locationTitle }}
                                </a>
                            </div>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start" style="gap: 8px; min-height: 80px; padding: 8px 0; width:200px">
                            <!-- Status -->
                            <v-chip :style="{
                                    backgroundColor: item.statusColor,
                                    color: 'white'
                                }"
                                    size="small"
                                    label>
                                {{ item.statusTitle }}
                            </v-chip>

                            <!-- Assigned To -->
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon size="small">mdi-account-group</v-icon>
                                <span class="text-body-2" style="line-height: 1.3;">{{ item.assigneeTeamName || 'Unassigned' }}</span>
                            </div>

                            <!-- Updated Date -->
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon size="small">mdi-calendar-clock</v-icon>
                                <span class="text-caption" style="line-height: 1.3;">{{ formatDate(item.updatedDate) }}</span>
                            </div>
                        </div>
                    </td>
                </tr>
            </template>

            <!-- Mobile Item -->
            <template #mobile-item="{ item }">
                <v-card class="mb-2" variant="flat">
                    <v-card-title class="d-flex justify-space-between align-center">
                        <div class="d-flex align-center">
                            <v-chip label variant="text">
                                <a @click.prevent="openItemUrl(item)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.clientId }}
                                </a>
                            </v-chip>
                        </div>

                        <v-chip :style="{
                                backgroundColor: item.statusColor,
                                color: 'white'
                            }"
                                label
                                size="small">
                            {{ item.statusTitle }}
                        </v-chip>
                    </v-card-title>

                    <v-card-text>
                        <div class="d-flex flex-column gap-2">
                            <div v-if="item.photo || item.responsePhoto" class="d-flex justify-center mb-2" @click="openPhotoDialog(item)">
                                <v-img :src="item.responsePhoto || item.photo"
                                       width="200"
                                       height="150"
                                       :aspect-ratio="4/3"
                                       contain
                                       class="rounded bg-grey-lighten-4 cursor-pointer" />
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('type') }}:</span>
                                <span>{{ item.issueType }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('category') }}:</span>
                                <span>{{ item.issueCategory }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('item') }}:</span>
                                <span>{{ item.issueItem }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('issueDescription') }}:</span>
                                <span>{{ item.issueDescription?.replace(/[\r\n]+/g, '; ') }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('responsibleParty') }}:</span>
                                <div class="d-flex flex-column align-end">
                                    <span>{{ item.responsibleParty }}</span>
                                    <v-chip variant="tonal" size="x-small" density="compact" class="mt-1">
                                        {{ item.priority }}
                                    </v-chip>
                                </div>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('dueBy') }}:</span>
                                <span>{{ formatDate(item.dueBy) }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('assignedTo') }}:</span>
                                <span>{{ item.assigneeTeamName }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('location') }}:</span>
                                <div class="d-flex flex-column align-end gap-1">
                                    <div>
                                        <v-icon icon="mdi-map" size="small" class="mr-1"></v-icon>
                                        <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}`)"
                                           href="#"
                                           style="color: inherit;">
                                            {{ item.drawingTitle }}
                                        </a>
                                    </div>
                                    <div>
                                        <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                                        <a @click.prevent="openUrl(`https://${apiHost}/${projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                                           href="#"
                                           style="color: inherit;">
                                            {{ item.locationTitle }}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('lastUpdated') }}:</span>
                                <span>{{ formatDate(item.updatedDate) }}</span>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </template>
        </DuckDbDataTable>

        <!-- The sibling report dialog: it collects the options; the parent's generateReport runs
             the report -->
        <ItemsGridPaginatedReportsDialog v-if="generateReport"
                                         v-model="showSummaryReportDialog"
                                         :generating="reportBusy"
                                         :disabled-modes="reportDisabledModes"
                                         :group-by-options="reportGroupByOptions"
                                         :order-by-options="reportOrderByOptions"
                                         @generate="handleGenerateReport" />
    </div>
</template>

<script src="./model.js"></script>
<style scoped src="./style.css"></style>
